import { jsxs, jsx } from "react/jsx-runtime";
import { useClassName } from "@contember/react-utils";
import { memo, forwardRef, useState, useEffect, useCallback, useRef } from "react";
import { assertDatetimeString } from "../Types/ControlValues.js";
import { splitDatetime } from "../Types/Serializer.js";
import { Divider } from "../../Divider/Divider.js";
import { useTextBasedInput } from "../Hooks/useTextBasedInput.js";
import { Stack } from "../../Stack/Stack.js";
import { useInputClassName } from "../Hooks/useInputClassName.js";
import { toViewClass } from "../../../utils/toViewClass.js";
function joinDatetime(date, time) {
  return `${date}T${time}`;
}
const DateTimeInputFallback = memo(forwardRef(({
  className,
  max,
  min,
  onChange,
  onValidationStateChange,
  value: _value,
  withTopToolbar,
  ...outerProps
}, forwardedRef) => {
  const value = _value ?? "";
  if (value) {
    assertDatetimeString(value);
  }
  if (max) {
    assertDatetimeString(max);
  }
  if (min) {
    assertDatetimeString(min);
  }
  const [state, setState] = useState(value);
  const [date, time] = splitDatetime(state);
  useEffect(() => {
    setState(value);
  }, [value]);
  useEffect(() => {
    onChange?.(state);
  }, [state, onChange]);
  const onDateChange = useCallback((event) => {
    setState(joinDatetime(event.target.value, time));
  }, [time]);
  const onTimeChange = useCallback((event) => {
    setState(joinDatetime(date, event.target.value));
  }, [date]);
  const dateError = useRef(void 0);
  const timeError = useRef(void 0);
  const changeValidationState = useCallback(() => {
    onValidationStateChange?.([dateError.current, timeError.current].filter(Boolean).join(" "));
  }, [onValidationStateChange]);
  const dateInputProps = useTextBasedInput({
    ...outerProps,
    distinction: "seamless",
    className: useClassName("input", className),
    onValidationStateChange: useCallback((error) => {
      dateError.current = error;
      changeValidationState();
    }, [changeValidationState]),
    value: date
  }, forwardedRef);
  const timeInputRef = useRef(null);
  const timeInputProps = useTextBasedInput({
    ...outerProps,
    distinction: "seamless",
    className: useClassName("input", className),
    onValidationStateChange: useCallback((error) => {
      timeError.current = error;
      changeValidationState();
    }, [changeValidationState]),
    value: time
  }, timeInputRef);
  const [maxDate, maxTime] = splitDatetime(max);
  const [minDate, minTime] = splitDatetime(min);
  return /* @__PURE__ */ jsxs(Stack, { gap: "large", horizontal: true, className: useInputClassName({
    ...outerProps,
    className: useClassName(["text-input", "datetime-input"], [
      toViewClass("withTopToolbar", withTopToolbar),
      className
    ])
  }), children: [
    /* @__PURE__ */ jsx(
      "input",
      {
        ...dateInputProps,
        max: maxDate,
        min: minDate,
        onChange: onDateChange,
        placeholder: outerProps.placeholder ?? void 0,
        type: "date"
      }
    ),
    /* @__PURE__ */ jsx(Divider, { gap: false }),
    /* @__PURE__ */ jsx(
      "input",
      {
        ...timeInputProps,
        max: date && date === maxDate ? maxTime : "",
        min: date && date === minDate ? minTime : "",
        onChange: onTimeChange,
        placeholder: void 0,
        type: "time"
      }
    )
  ] });
}));
DateTimeInputFallback.displayName = "DateTimeInputFallback";
export {
  DateTimeInputFallback
};
//# sourceMappingURL=DateTimeInputFallback.js.map
